import type { GraphModification, DescriptorDatatype, Graph } from "@lr-notitia/notitia-engineer/schemas";
import { Ref, computed } from "vue";
import { api } from "@lr-notitia/notitia-engineer/client";
import type { Terminology } from "@lr-notitia/notitia-dictionary/schemas";
import { handleGraphModification } from "./utils";
import { LanguageCode } from "iso-639-1";
import { useDomainStore } from "../domain";
import { useSymbolsStore } from "../symbols";
import * as Metrics from "../metrics";

let symbolsStore: ReturnType<typeof useSymbolsStore>;
let domainStore: ReturnType<typeof useDomainStore>;
function init() {
	symbolsStore = useSymbolsStore();
	domainStore = useDomainStore();
}

export const datatypes = (graph: Ref<Graph>) =>
	computed<DescriptorDatatype[]>(() => {
		if (!symbolsStore) init();
		return (
			Object.values(graph.value.nodes).filter((node) => node.type === "datatype") as DescriptorDatatype[]
		).sort((a, b) =>
			symbolsStore
				.term({ id: a.refersTo, terminology: a.terminology || {} })
				.toLocaleLowerCase()
				.localeCompare(
					symbolsStore.term({ id: b.refersTo, terminology: b.terminology || {} }).toLocaleLowerCase()
				)
		);
	});
export function datatype(domainId: Ref<string>, graph: Ref<Graph>) {
	return {
		byId: (id: string) => {
			Metrics.recordAccess(id);
			return graph.value.nodes[id] as DescriptorDatatype | undefined;
		},
		add: (data: { refersTo: string; localTerminology: Terminology }) => add(domainId, graph, data),
		update: (datatype: DescriptorDatatype) => update(domainId, graph, datatype),
		remove: (id: string) => remove(domainId, graph, id),
		search: (query: string, lang?: LanguageCode) => search(graph, query, lang),
		isImmutable: (id: string) => isImmutable(id),
		duplicate: (id: string) => duplicate(domainId, graph, id),
	};
}

function add(
	domainId: Ref<string>,
	graph: Ref<Graph>,
	data: { refersTo: string; localTerminology: Terminology }
): Promise<GraphModification> {
	return new Promise((resolve, reject) => {
		api()
			.domain(domainId.value)
			.datatypes()
			.post(data)
			.then((data) => {
				handleGraphModification(graph, data);
				resolve(data);
			})
			.catch(() => reject());
	});
}
function update(domainId: Ref<string>, graph: Ref<Graph>, datatype: DescriptorDatatype): Promise<GraphModification> {
	return new Promise((resolve, reject) => {
		if (!graph.value.nodes[datatype.id]) return reject();
		api()
			.domain(domainId.value)
			.datatype(datatype.id)
			.put(datatype)
			.then((data) => {
				handleGraphModification(graph, data);
				resolve(data);
			})
			.catch(() => reject());
	});
}
function remove(domainId: Ref<string>, graph: Ref<Graph>, id: string): Promise<GraphModification> {
	return new Promise((resolve, reject) => {
		if (!graph.value.nodes[id]) return reject();
		api()
			.domain(domainId.value)
			.datatype(id)
			.del()
			.then((data) => {
				handleGraphModification(graph, data);
				resolve(data);
			})
			.catch(() => reject());
	});
}

function search(graph: Ref<Graph>, query: string, lang?: LanguageCode): DescriptorDatatype[] {
	if (!symbolsStore || !domainStore) init();
	const language = lang || domainStore.languagePreference;
	const datatypes = Object.values(graph.value.nodes).filter(
		(node) => node.type === "datatype"
	) as DescriptorDatatype[];
	return datatypes.filter((datatype) => {
		return (
			symbolsStore
				.term({ id: datatype.refersTo, terminology: datatype.terminology || {}, lang: language })
				.toLocaleLowerCase()
				.includes(query.toLocaleLowerCase()) ||
			symbolsStore
				.synonyms({ id: datatype.refersTo, terminology: datatype.terminology || {}, lang: language })
				.some((synonym) => synonym.toLocaleLowerCase().includes(query.toLocaleLowerCase())) ||
			symbolsStore
				.term({ id: datatype.refersTo, terminology: datatype.terminology || {}, lang: "en" })
				.toLocaleLowerCase()
				.includes(query.toLocaleLowerCase()) ||
			symbolsStore
				.synonyms({ id: datatype.refersTo, terminology: datatype.terminology || {}, lang: "en" })
				.some((synonym) => synonym.toLocaleLowerCase().includes(query.toLocaleLowerCase()))
		);
	});
}

function isImmutable(id: string): boolean {
	return immutableDatatypeIds.includes(id);
}

function duplicate(domainId: Ref<string>, graph: Ref<Graph>, id: string): Promise<GraphModification> {
	return new Promise((resolve, reject) => {
		if (!graph.value.nodes[id]) return reject();
		api()
			.domain(domainId.value)
			.datatype(id)
			.duplicate()
			.then((data) => {
				handleGraphModification(graph, data);
				resolve(data);
			})
			.catch(() => reject());
	});
}

const immutableDatatypeIds = ["boolean", "string", "number", "date", "time", "timestamp", "array", "reference"];
